// Shared viewport bbox debounce + parse util for map layer-state hooks.
// Used by use-transport-layer-state, use-administrative-area-layer-state, and
// use-map-poi-state so bbox debounce/parse logic lives in exactly one place.
import * as React from 'react';

export function useDebouncedBbox(bbox: string | null, delayMs: number): string | null {
  const [debouncedBbox, setDebouncedBbox] = React.useState<string | null>(null);
  const initializedRef = React.useRef(false);
  React.useEffect(() => {
    if (bbox === null) {
      setDebouncedBbox(null);
      initializedRef.current = false;
      return;
    }
    if (!initializedRef.current) {
      // First non-null bbox: set immediately, no debounce needed
      initializedRef.current = true;
      setDebouncedBbox(bbox);
      return;
    }
    const id = setTimeout(() => setDebouncedBbox(bbox), delayMs);
    return () => clearTimeout(id);
  }, [bbox, delayMs]);
  return debouncedBbox;
}

export interface BboxCoords {
  minLon: number;
  minLat: number;
  maxLon: number;
  maxLat: number;
}

export function parseBbox(bbox: string): BboxCoords | null {
  const parts = bbox.split(',').map(Number);
  if (parts.length !== 4 || parts.some(isNaN)) return null;
  // Casting is safe: length === 4 is asserted above and elements are numbers (NaN checked).
  return {
    minLon: parts[0] as number,
    minLat: parts[1] as number,
    maxLon: parts[2] as number,
    maxLat: parts[3] as number,
  };
}

// Fallback bbox coords (used when parsedBbox is null + query is disabled).
export const ZERO_BBOX: BboxCoords = { minLon: 0, minLat: 0, maxLon: 0, maxLat: 0 };

/**
 * Parses a bbox string into a [west, south, east, north] tuple for MapView's
 * `initialBounds` prop (see packages/ui/src/components/map-view.tsx). Reuses
 * parseBbox's split/NaN validation, then additionally rejects inverted or
 * out-of-range coordinates — malformed or hand-edited `viewBbox` URL params
 * must never reach MapLibre's fitBounds, which throws on invalid bounds.
 */
export function parseBboxTuple(bbox: string | undefined): [number, number, number, number] | null {
  if (!bbox) return null;
  const parsed = parseBbox(bbox);
  if (!parsed) return null;
  const { minLon, minLat, maxLon, maxLat } = parsed;
  if (minLon >= maxLon || minLat >= maxLat) return null;
  if (Math.abs(minLon) > 180 || Math.abs(maxLon) > 180) return null;
  if (Math.abs(minLat) > 90 || Math.abs(maxLat) > 90) return null;
  return [minLon, minLat, maxLon, maxLat];
}
